// One company on the weekly statement (Paystack plan 8, PR 14): what Zuttle
// owes it by EFT (with "Mark paid"), what Paystack paid it at source, and its
// charter money. Only the first is owed.
//
// A week marked paid at less than it now owes (`paidAmount` below
// `totalOwed`, e.g. a week paid before tip rows were counted) is "Part paid",
// and offers "Mark paid" for the rest as well as Undo. When opened, a paid
// week lists each EFT (`eftPayments`: its own amount, date and reference).
// Undo asks first, naming what it removes: on a topped-up week only the
// latest top-up, and the earlier payments stay recorded.

import React from 'react';
import { ChevronDown, ChevronRight, Check, RotateCcw, Users } from 'lucide-react';
import { rands, fmtDate } from './money';

const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;

// An older response has no `paidAtSource` or `charter`: show them empty.
const EMPTY_SIDE = {
  rides: 0, fares: 0, zuttleShare: 0, operatorFee: 0, companyEarnings: 0, driverEarnings: 0,
  tips: 0, tipCount: 0, tipFee: 0, total: 0, drivers: [],
};
const EMPTY_CHARTER = { entries: [], chargedCents: 0, refundedCents: 0, commissionCents: 0, operatorCents: 0, paidAtSourceCents: 0, simulationCents: 0 };

/** What is still owed on a week marked paid at less than its total, or 0. */
export const stillOwed = (c) => (c.paid && typeof c.paidAmount === 'number' && c.paidAmount < c.totalOwed
  ? c.totalOwed - c.paidAmount : 0);

/** The week's EFTs, oldest first. An older response has no list: one EFT of `paidAmount`. */
export function paymentsOf(c) {
  if (!c.paid) return [];
  if (c.eftPayments && c.eftPayments.length) return c.eftPayments;
  const amountCents = typeof c.paidAmount === 'number' ? c.paidAmount : c.totalOwed;
  return [{ amountCents, reference: c.paidReference || null, paidAt: c.paidAt || null }];
}

/** What Undo asks before it runs: the amount it removes, and what stays. */
export function undoQuestion(c) {
  const payments = paymentsOf(c);
  if (payments.length < 2) {
    const amount = payments.length ? payments[0].amountCents : c.paidAmount;
    return `Remove the payment of ${rands(amount)}? The week then shows unpaid.`;
  }
  const latest = payments[payments.length - 1];
  const earlier = payments.slice(0, -1);
  if (earlier.length === 1) {
    return `Remove the top-up of ${rands(latest.amountCents)}? The first payment of ${rands(earlier[0].amountCents)} stays recorded.`;
  }
  const kept = earlier.reduce((sum, p) => sum + p.amountCents, 0);
  return `Remove the latest top-up of ${rands(latest.amountCents)}? The ${earlier.length} earlier payments, ${rands(kept)} in all, stay recorded.`;
}

/**
 * The reference on the "Paid" badge: the bank reference, or with none Zuttle's
 * own payout number, named as that ("payout PO-CMP-..."), never as a bank reference.
 */
const shownReference = (c) => c.paidReference || (c.payoutNumber ? `payout ${c.payoutNumber}` : null);

const paymentLine = (p) => `${rands(p.amountCents)}${p.paidAt ? ` on ${fmtDate(p.paidAt)}` : ''}, ${p.reference ? `reference ${p.reference}` : 'no reference'}`;

const DriverTable = ({ drivers, empty }) => (
  <table className="w-full text-sm">
    <thead>
      <tr className="text-left text-gray-500">
        <th className="py-2 font-medium">Driver</th>
        <th className="py-2 font-medium text-right">Rides</th>
        <th className="py-2 font-medium text-right">Earnings</th>
        <th className="py-2 font-medium text-right">Tips</th>
        <th className="py-2 font-medium text-right">Total</th>
      </tr>
    </thead>
    <tbody className="divide-y divide-gray-200">
      {drivers.length === 0 ? (
        <tr><td colSpan={5} className="py-3 text-gray-400">{empty}</td></tr>
      ) : drivers.map((d) => (
        <tr key={d.driverId}>
          <td className="py-2 text-gray-900">{d.driverName}</td>
          <td className="py-2 text-right text-gray-600">{d.rides}</td>
          <td className="py-2 text-right text-gray-600">{rands(d.earnings)}</td>
          <td className="py-2 text-right text-gray-600">{rands(d.tips)}</td>
          <td className="py-2 text-right font-medium text-gray-900">{rands(d.total)}</td>
        </tr>
      ))}
    </tbody>
  </table>
);

const Section = ({ title, children }) => (
  <div className="pt-3">
    <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">{title}</p>
    {children}
  </div>
);

const CharterTable = ({ entries }) => (
  <table className="w-full text-sm">
    <thead>
      <tr className="text-left text-gray-500">
        <th className="py-2 font-medium">Hire</th>
        <th className="py-2 font-medium">Payment</th>
        <th className="py-2 font-medium text-right">Amount</th>
        <th className="py-2 font-medium text-right">Zuttle's share</th>
        <th className="py-2 font-medium text-right">Operator's share</th>
        <th className="py-2 font-medium text-right">Paid by</th>
      </tr>
    </thead>
    <tbody className="divide-y divide-gray-200">
      {entries.map((e) => {
        const sign = e.kind === 'refund' ? -1 : 1;
        return (
          <tr key={e.reference}>
            <td className="py-2 text-gray-900">{e.bookingCode}</td>
            <td className="py-2 text-gray-600 capitalize">{e.kind} · {fmtDate(e.settledAt)}</td>
            <td className="py-2 text-right text-gray-600">{rands(sign * e.amountCents)}</td>
            <td className="py-2 text-right text-gray-600">{rands(sign * e.commissionCents)}</td>
            <td className="py-2 text-right font-medium text-gray-900">{rands(sign * e.operatorCents)}</td>
            <td className="py-2 text-right text-gray-600">{e.gateway === 'paystack' ? 'Paystack' : 'Simulation'}</td>
          </tr>
        );
      })}
    </tbody>
  </table>
);

const CompanyWeekRow = ({ c, expanded, onToggle, busy, onMarkPaid, onUnmark }) => {
  const atSource = { ...EMPTY_SIDE, ...c.paidAtSource };
  const atSourceDrivers = atSource.drivers || [];
  const charter = { ...EMPTY_CHARTER, ...c.charter };
  const entries = charter.entries || [];
  const drivers = c.drivers || [];
  // A week of only refunds of trips counted earlier (#640) has no rides but an EFT breakdown.
  const owesByEft = c.rides > 0 || c.tipCount > 0 || c.totalOwed < 0 || c.owedToZuttle > 0;
  const paidAtSourceShown = atSource.rides > 0 || atSource.tipCount > 0;
  const charterShown = entries.length > 0;
  const driverCount = new Set([...drivers, ...atSourceDrivers].map((d) => d.driverId)).size;
  const rest = stillOwed(c);
  const canMarkPaid = c.totalOwed > 0 && (!c.paid || rest > 0);
  const payments = paymentsOf(c);
  const undo = () => {
    if (window.confirm(undoQuestion(c))) onUnmark();
  };

  return (
    <div>
      <div className="flex items-center gap-3 p-4">
        <button onClick={onToggle} aria-label={expanded ? 'Hide details' : 'Show details'}
          className="p-1 rounded hover:bg-gray-100 text-gray-500">
          {expanded ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
        </button>
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-gray-900 truncate">{c.companyName}</p>
          <p className="text-xs text-gray-500">
            {plural(c.rides, 'ride', 'rides')} · <Users size={11} className="inline -mt-0.5" /> {plural(driverCount, 'driver', 'drivers')}
            {c.tips > 0 ? ` · ${rands(c.tips)} tips` : ''}
            {paidAtSourceShown ? ` · ${rands(atSource.total)} paid by Paystack` : ''}
            {charterShown ? ` · ${rands(charter.operatorCents)} charter` : ''}
          </p>
        </div>
        <div className="text-right">
          <p className="font-bold text-gray-900">{rands(c.totalOwed)}</p>
          {rest > 0
            ? <span className="text-xs font-medium text-orange-700 bg-orange-50 px-2 py-0.5 rounded-full">
                Part paid · {rands(rest)} still owed
              </span>
            : c.paid
            ? <span className="inline-flex items-center gap-1 text-xs font-medium text-green-700 bg-green-50 px-2 py-0.5 rounded-full">
                <Check size={12} /> Paid{shownReference(c) ? ` · ${shownReference(c)}` : ''}
              </span>
            : c.totalOwed > 0
              ? <span className="text-xs font-medium text-yellow-700 bg-yellow-50 px-2 py-0.5 rounded-full">Unpaid</span>
              : c.totalOwed === 0 && <span className="text-xs font-medium text-gray-600 bg-gray-100 px-2 py-0.5 rounded-full">Nothing owed by EFT</span>}
          {/* Refunds this week of trips already paid for (#640): the company owes Zuttle this, by hand. */}
          {c.owedToZuttle > 0 && (
            <span className="block mt-1 text-xs font-medium text-red-700 bg-red-50 px-2 py-0.5 rounded-full">
              Owes Zuttle {rands(c.owedToZuttle)} (refunds this week)
            </span>
          )}
        </div>
        {canMarkPaid && (
          <button onClick={onMarkPaid}
            className="px-3 py-1.5 text-xs font-medium text-white bg-emerald-600 rounded-lg hover:bg-emerald-700">
            Mark paid
          </button>
        )}
        {c.paid && (
          <button onClick={undo} disabled={busy}
            className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50">
            <RotateCcw size={13} /> Undo
          </button>
        )}
      </div>

      {expanded && (
        <div className="bg-gray-50 px-4 pb-4 divide-y divide-gray-200">
          {(owesByEft || (!paidAtSourceShown && !charterShown)) && (
            <Section title="Owed by EFT">
              <DriverTable drivers={drivers} empty="No driver-attributed rides." />
              <p className="text-xs text-gray-400 mt-2">This is the report the company uses to pay its drivers. The company total also includes the company's own share ({rands(c.companyEarnings)}). Fares {rands(c.fares)}, Zuttle's share {rands(c.zuttleShare)}.</p>
              {payments.length > 0 && (
                <div className="text-xs text-gray-500 mt-2">
                  <p>{payments.length > 1 ? `Paid in ${payments.length} EFTs, ${rands(c.paidAmount)} in all:` : 'Paid by EFT:'}</p>
                  <ul className="list-disc pl-4">
                    {payments.map((p, i) => <li key={i}>{paymentLine(p)}</li>)}
                  </ul>
                </div>
              )}
            </Section>
          )}
          {paidAtSourceShown && (
            <Section title="Paid by Paystack at source (not owed)">
              <DriverTable drivers={atSourceDrivers} empty="No driver-attributed rides." />
              <p className="text-xs text-gray-400 mt-2">
                Paystack paid {rands(atSource.total)} to the operator's subaccount: fares {rands(atSource.fares)}, less Zuttle's share {rands(atSource.zuttleShare)}
                {atSource.operatorFee > 0 ? `, less the Paystack fee the operator bore (${rands(atSource.operatorFee)})` : ''}
                , plus tips {rands(atSource.tips)}{atSource.tipFee > 0 ? ` (after ${rands(atSource.tipFee)} in card fees)` : ''}. The company's own share is {rands(atSource.companyEarnings)}.
              </p>
            </Section>
          )}
          {charterShown && (
            <Section title="Charter (not owed)">
              <CharterTable entries={entries} />
              <p className="text-xs text-gray-400 mt-2">
                Operator's share {rands(charter.operatorCents)}: {rands(charter.paidAtSourceCents)} paid by Paystack at source
                {charter.simulationCents !== 0 ? `, ${rands(charter.simulationCents)} in simulation (no money moved)` : ''}.
              </p>
            </Section>
          )}
        </div>
      )}
    </div>
  );
};

export default CompanyWeekRow;
